import {
  Box,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Input,
  InputGroup,
  InputRightElement,
  TableContainer,
  Spinner,
  Badge,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Flex,
  ButtonGroup,
} from "@chakra-ui/react";
import { SearchIcon, AddIcon } from "@chakra-ui/icons";
import { useState, useEffect } from "react";
import Navbar from "../../Navbar/Navbar";
import Sidebar from "../../Sidebar";
import { Link } from "react-router-dom";
import axios from "axios";
import Pagination from "../../Common/Pagination";
import { useUser } from "../../../context/UserContext";
import { FiMoreVertical, FiEye, FiLock, FiSlash, FiEdit, FiDollarSign } from "react-icons/fi";
import ChangePasswordModal from "../Modals/ChangePasswordModal";
import BlockUserModal from "../Modals/BlockUserModal";
import EditUserModal from "../Modals/EditUserModal";
import CreateUserModal from "../Modals/CreateUserModal";
import AdjustWalletModal from "../Modals/AdjustWalletModal";

const SIDEBAR_WIDTH = "240px";
const ITEMS_PER_PAGE = 7;

export default function AllUsers() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [selectedRole, setSelectedRole] = useState("all");
  const { user } = useUser();

  const [selectedUser, setSelectedUser] = useState(null);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isBlockOpen, setIsBlockOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isAdjustOpen, setIsAdjustOpen] = useState(false);

  const roleOptions = [
    { label: "All", value: "all" },
    { label: "Player", value: "player" },
    // { label: "Agent", value: "agent" },
    { label: "Admin", value: "admin" },
    { label: "Super Admin", value: "super-admin" },
  ];

  const openChangePassword = (u) => {
    setSelectedUser(u);
    setIsChangePasswordOpen(true);
  };
  const openBlockUser = (u) => {
    setSelectedUser(u);
    setIsBlockOpen(true);
  };
  const openEditUser = (u) => {
    setSelectedUser(u);
    setIsEditOpen(true);
  };
  const openAdjustWallet = (u) => {
    setSelectedUser(u);
    setIsAdjustOpen(true);
  };

  // Debounce search
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchUsers();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [search, page, sortBy, sortOrder, selectedRole, limit]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const params = {
        page: page,
        limit: limit,
        search: search,
        sort: sortBy,
        order: sortOrder,
      };

      if (selectedRole !== "all") {
        params.role = selectedRole;
      }

      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/users`,
        {
          params,
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (data.success) {
        setUsers(data.users || []);
        setTotalPages(data.totalPages || 1);
        setTotalUsers(data.totalUsers || 0);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error("Failed to fetch users", error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("desc");
    }
    setPage(1);
  };

  const SortIcon = ({ column }) => {
    if (sortBy !== column) return null;
    return sortOrder === "asc" ? " ▲" : " ▼";
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
  };

  const data = users;

  return (
    <>
      <Navbar />
      <Sidebar />
      <Box
        p={{ base: 4, md: 8 }}
        bg="#f8f9fd"
        minH="100vh"
        mt="80px"
        ml={{ base: 0, md: SIDEBAR_WIDTH }}
      >
        {/* HEADER */}
        <Flex
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          mb={4}
          align={{ base: "flex-start", md: "center" }}
          gap={4}
        >
          <Box>
            <Flex align="center" gap={4}>
              <Text fontSize="2xl" fontWeight="700" color="#344767" mb={0}>
                All Users
              </Text>
              <Button
                leftIcon={<AddIcon />}
                colorScheme="blue"
                size="sm"
                onClick={() => setIsCreateOpen(true)}
              >
                Add User
              </Button>
            </Flex>
          </Box>

          <InputGroup
            maxW={{ base: "100%", md: "350px" }}
            size="md"
            mt={{ base: 2, md: 0 }}
          >
            <Input
              placeholder="Username / Email / Mobile"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              bg="white"
              borderRadius="8px"
              border="1px solid #e2e8f0"
              _focus={{
                borderColor: "#1f15daff",
                boxShadow: "0 0 0 1px #1f15daff",
              }}
            />
            <InputRightElement color="gray.400">
              <SearchIcon />
            </InputRightElement>
          </InputGroup>
        </Flex>

        {/* Role Filter */}
        <Flex justify="flex-start" mb={4} width="100%">
          <ButtonGroup isAttached variant="outline" size="sm">
            {roleOptions.map((role) => (
              <Button
                key={role.value}
                bg={selectedRole === role.value ? "blue.500" : "white"}
                color={selectedRole === role.value ? "white" : "gray.600"}
                borderColor={
                  selectedRole === role.value ? "blue.500" : "gray.200"
                }
                zIndex={selectedRole === role.value ? 1 : 0}
                _hover={{
                  bg: selectedRole === role.value ? "blue.600" : "gray.50",
                  borderColor:
                    selectedRole === role.value ? "blue.600" : "gray.300",
                }}
                _focus={{ boxShadow: "none" }}
                onClick={() => {
                  setSelectedRole(role.value);
                  setPage(1);
                }}
              >
                {role.label}
              </Button>
            ))}
          </ButtonGroup>
        </Flex>

        {/* TABLE CONTAINER */}
        <Box
          bg="white"
          borderRadius="12px"
          boxShadow="0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
          overflow="hidden"
          border="1px solid #f0f2f5"
        >
          <TableContainer>
            <Table variant="simple">
              <Thead bg="#1f15da">
                <Tr>
                  <Th
                    color="white"
                    fontSize="xs"
                    py={4}
                    border="none"
                    cursor="pointer"
                    onClick={() => handleSort("username")}
                  >
                    USER <SortIcon column="username" />
                  </Th>
                  <Th
                    color="white"
                    fontSize="xs"
                    py={4}
                    border="none"
                    cursor="pointer"
                    onClick={() => handleSort("email")}
                  >
                    EMAIL <SortIcon column="email" />
                  </Th>
                  <Th
                    color="white"
                    fontSize="xs"
                    py={4}
                    border="none"
                    cursor="pointer"
                    onClick={() => handleSort("mobile")}
                  >
                    MOBILE <SortIcon column="mobile" />
                  </Th>
                  <Th
                    color="white"
                    fontSize="xs"
                    py={4}
                    border="none"
                    cursor="pointer"
                    onClick={() => handleSort("role")}
                  >
                    ROLE <SortIcon column="role" />
                  </Th>
                  <Th
                    color="white"
                    fontSize="xs"
                    py={4}
                    border="none"
                    cursor="pointer"
                    onClick={() => handleSort("status")}
                  >
                    STATUS <SortIcon column="status" />
                  </Th>
                  <Th
                    color="white"
                    fontSize="xs"
                    py={4}
                    border="none"
                    cursor="pointer"
                    onClick={() => handleSort("createdAt")}
                  >
                    JOINED AT <SortIcon column="createdAt" />
                  </Th>
                  <Th color="white" fontSize="xs" py={4} border="none">
                    BALANCE
                  </Th>
                  <Th color="white" fontSize="xs" py={4} border="none">
                    ACTION
                  </Th>
                </Tr>
              </Thead>

              <Tbody>
                {loading ? (
                  <Tr>
                    <Td colSpan={8} textAlign="center" py={8}>
                      <Spinner />
                    </Td>
                  </Tr>
                ) : data.length === 0 ? (
                  <Tr>
                    <Td colSpan={8} textAlign="center" py={8}>
                      No users found.
                    </Td>
                  </Tr>
                ) : (
                  data.map((u) => (
                    <Tr key={u._id || u.id} _hover={{ bg: "#f8f9fa" }}>
                      <Td py={4}>
                        <Text fontWeight="700" fontSize="sm" color="#344767">
                          {u.name || "N/A"}
                        </Text>
                        <Text color="#5e72e4" fontSize="xs">
                          {u.username}
                        </Text>
                      </Td>

                      <Td py={4} color="#67748e" fontSize="xs">
                        {u.email}
                      </Td>

                      <Td py={4} color="#67748e" fontSize="xs">
                        {u.mobile}
                      </Td>

                      <Td py={4} fontWeight="700" color="#344767" fontSize="sm">
                        <Badge>{u.role}</Badge>
                      </Td>

                      <Td py={4}>
                        <Badge
                          colorScheme={u.status === "active" ? "green" : "red"}
                        >
                          {u.status}
                        </Badge>
                      </Td>

                      <Td py={4} color="#67748e" fontSize="xs">
                        <Box>{new Date(u.createdAt).toLocaleDateString()}</Box>
                        <Box color="#8898aa">
                          {new Date(u.createdAt).toLocaleTimeString()}
                        </Box>
                      </Td>

                      <Td py={4} fontWeight="700" color="#344767" fontSize="sm">
                        ₹{u.wallet?.balance?.toLocaleString("en-IN") || 0}
                      </Td>

                      <Td py={4}>
                        <Menu>
                          <MenuButton
                            as={IconButton}
                            icon={<FiMoreVertical />}
                            variant="ghost"
                            size="sm"
                          />
                          <MenuList>
                            <MenuItem
                              icon={<FiEye />}
                              as={Link}
                              to={`/users/${u._id}`}
                            >
                              View User
                            </MenuItem>
                            <MenuItem
                              icon={<FiEdit />}
                              onClick={() => openEditUser(u)}
                            >
                              Edit User
                            </MenuItem>
                            <MenuItem
                              icon={<FiLock />}
                              onClick={() => openChangePassword(u)}
                            >
                              Change Password
                            </MenuItem>
                            <MenuItem
                              icon={<FiDollarSign />}
                              onClick={() => openAdjustWallet(u)}
                            >
                              Adjust Credits
                            </MenuItem>
                            <MenuItem
                              icon={<FiSlash />}
                              color="red.500"
                              onClick={() => openBlockUser(u)}
                            >
                              Block User
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>

        {/* PAGINATION */}
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalEntries={totalUsers}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={handleLimitChange}
        />
      </Box>

      {/* MODALS */}
      {selectedUser && (
        <>
          <ChangePasswordModal
            isOpen={isChangePasswordOpen}
            onClose={() => setIsChangePasswordOpen(false)}
            user={selectedUser}
          />
          <BlockUserModal
            isOpen={isBlockOpen}
            onClose={() => setIsBlockOpen(false)}
            user={selectedUser}
          />
          <EditUserModal
            isOpen={isEditOpen}
            onClose={() => setIsEditOpen(false)}
            user={selectedUser}
          />
          <AdjustWalletModal
            isOpen={isAdjustOpen}
            onClose={() => {
              setIsAdjustOpen(false);
              fetchUsers();
            }}
            user={selectedUser}
            onUpdate={fetchUsers}
          />
        </>
      )}
      <CreateUserModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onSuccess={fetchUsers}
      />
    </>
  );
}
